import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { defaultTo } from 'lodash';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { handleUnexpectedError } from '@/app/errors';
import { useCurrentUser } from '@/app/hooks/use-current-user';
import { useAppForm } from '@/app/lib/tanstack-form';
import { routeMetadata } from '@/app/lib/tanstack-router';
import { saveNotificationPreferences, updateProfile } from '@/app/network/firefly-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { CheckboxField, CheckboxGroup } from '@/common/components/checkbox';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@/common/components/dialog';
import { Label } from '@/common/components/fieldset';
import { Heading, Subheading } from '@/common/components/heading';
import { Nullish } from '@/common/components/nullish';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/common/components/tooltip';
import { PenIcon, PlusIcon, TrashIcon } from '@/icons/fa/regular';
import {
  Setting,
  SettingContent,
  SettingName,
  Settings,
  SettingsSection,
  SettingsSet
} from '@/settings/components/settings';
import { SettingsSidebar } from '@/settings/components/settings-sidebar';
import { useRemoveSms } from '@/settings/queries';
import { notificationPreferenceKeys, useUserNotificationPreferences } from '@/settings/queries/notification-preferences';
import { ContentSection } from '@/shared/components/content-section';
import { Toast } from '@/shared/components/toast';
import { userKeys } from '@/shared/user/queries';
import { fullName } from '@/shared/users';

export const Route = createFileRoute('/settings/_layout/user-profile/')({
  head: () => ({
    meta: routeMetadata('User Profile')
  }),
  component: UserProfilePage
});

function UserProfilePage() {
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const notificationPreferences = useUserNotificationPreferences(user.id);
  const [removeSmsDialogIsOpen, setRemoveSmsDialogIsOpen] = useState(false);

  const removeSmsMutation = useRemoveSms();

  const updateGeneralNotificationsMutation = useMutation({
    mutationFn: async ({ email, push, newsletter }: typeof notificationPreferencesForm.state.values) => {
      const { id: userId, first_name: firstName, last_name: lastName } = user;
      const updateProfilePromise = updateProfile(
        userId,
        firstName,
        lastName,
        navigator.language,
        newsletter
      );

      const saveNotificationPreferencesPromise = saveNotificationPreferences(userId, email, push);

      await Promise.all([updateProfilePromise, saveNotificationPreferencesPromise]);
    }
  });

  const notificationPreferencesForm = useAppForm({
    defaultValues: {
      email: defaultTo(notificationPreferences.data?.[0].selected_delivery_methods?.includes('email'), false),
      push: defaultTo(notificationPreferences.data?.[0].selected_delivery_methods?.includes('push'), false),
      newsletter: defaultTo(user.newsletter, false)
    },
    onSubmit: async ({ value }) => {
      await updateGeneralNotificationsMutation.mutateAsync(
        value,
        {
          onSuccess: async () => {
            toast.success(Toast, {
              data: {
                title: 'Notification Preferences Updated',
                content: 'Your notification preferences have been updated.'
              }
            });
            await queryClient.invalidateQueries({ queryKey: userKeys.authenticated() });
            await queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.userNotificationPreferences(user.id) });
          },
          onError: error => handleUnexpectedError(error)
        }
      );
    }
  });

  const handleRemoveSmsConfirm = async () => {
    await removeSmsMutation.mutateAsync(
      {},
      {
        onSuccess: async () => {
          toast.success(Toast, {
            data: {
              title: 'SMS Phone Number Removed',
              content: 'Your SMS phone number has been removed from your profile.'
            }
          });

          /*await queryClient.invalidateQueries({
            queryKey: userKeys.authenticated()
          });*/

          await queryClient.refetchQueries({
            queryKey: userKeys.authenticated()
          });

          setRemoveSmsDialogIsOpen(false);
        },
        onError: error => handleUnexpectedError(error)
      }
    );
  };

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <SettingsSidebar active="user-profile"/>
      <ContentSection bg="white">
        <Heading>User Profile</Heading>

        <form onSubmit={
          async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await notificationPreferencesForm.handleSubmit(event);
          }
        }
        >
          <Settings>
            <SettingsSection>
              <SettingsSet>
                <Setting>
                  <SettingName valign="center">Full Name</SettingName>
                  <SettingContent>
                    <div className="text-gray-900">{fullName(user.first_name, user.last_name)}</div>
                    <div>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button plain to="/settings/user-profile/change-name">
                            <PenIcon/>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Change Name
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </SettingContent>
                </Setting>

                <Setting>
                  <SettingName valign="center">Email Address</SettingName>
                  <SettingContent>
                    <div className="text-gray-900">
                      <Nullish value={user.email}/>
                    </div>
                    <div>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button plain to="/settings/user-profile/change-email">
                            <PenIcon/>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Change Email Address
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </SettingContent>
                </Setting>

                <Setting>
                  <SettingName valign="center">Password</SettingName>
                  <SettingContent>
                    <div className="text-gray-900">********</div>
                    <div>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button plain to="/settings/user-profile/change-password">
                            <PenIcon/>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Change Password
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </SettingContent>
                </Setting>

                <Setting>
                  <SettingName valign="center">SMS Phone Number</SettingName>
                  <SettingContent>
                    <div className="text-gray-900">
                      <Nullish value={user.sms_phone_number_formatted} />
                    </div>
                    <div>
                      {user.sms_phone_number ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button plain to="/settings/user-profile/update-sms">
                              <PenIcon/>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Update SMS Phone Number
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button plain to="/settings/user-profile/update-sms">
                              <PlusIcon/>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Set Up SMS Phone Number
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {user.sms_phone_number && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button plain onClick={() => setRemoveSmsDialogIsOpen(true)}>
                              <TrashIcon/>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Remove SMS Phone Number
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </SettingContent>
                </Setting>
              </SettingsSet>
            </SettingsSection>

            <SettingsSection>
              <Subheading>General Notifications</Subheading>
              <p className="text-sm text-gray-400">Receive general and emergency announcements.</p>

              <SettingsSet>
                <Setting>
                  <SettingName>Delivery Methods</SettingName>
                  <SettingContent>
                    <CheckboxGroup>
                      <notificationPreferencesForm.AppField name="email">
                        {field => (
                          <CheckboxField>
                            <field.FormCheckbox />
                            <Label>Email</Label>
                          </CheckboxField>
                        )}
                      </notificationPreferencesForm.AppField>

                      <notificationPreferencesForm.AppField name="push">
                        {field => (
                          <CheckboxField>
                            <field.FormCheckbox />
                            <Label>Push Notifications</Label>
                          </CheckboxField>
                        )}
                      </notificationPreferencesForm.AppField>
                    </CheckboxGroup>
                  </SettingContent>
                </Setting>

                <Setting>
                  <SettingName>Newsletter</SettingName>
                  <SettingContent>
                    <notificationPreferencesForm.AppField name="newsletter">
                      {field => (
                        <CheckboxField>
                          <field.FormCheckbox />
                          <Label>Email me <em>The Current</em></Label>
                        </CheckboxField>
                      )}
                    </notificationPreferencesForm.AppField>
                  </SettingContent>
                </Setting>
              </SettingsSet>
            </SettingsSection>
          </Settings>

          <ButtonGroup>
            <notificationPreferencesForm.Subscribe selector={state => state.isSubmitting}>
              {isSubmitting => (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              )}
            </notificationPreferencesForm.Subscribe>
          </ButtonGroup>
        </form>
      </ContentSection>

      <Dialog open={removeSmsDialogIsOpen} onClose={setRemoveSmsDialogIsOpen}>
        <DialogTitle>Remove SMS Phone Number</DialogTitle>
        <DialogDescription>
          Are you sure you want to remove your SMS phone number? You will no longer receive any notifications via SMS.
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setRemoveSmsDialogIsOpen(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleRemoveSmsConfirm}>Remove</Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}
